import * as React from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import SpaceFilters2, { type SpaceFilters2Handle } from '~/client/components/browse-view/filters/SpaceFilters'
import type { FiltersState } from '~/client/common/filters'
import { countAppliedFilters, filtersToParams } from '~/client/common/filters'
import { useAuthStore } from '~/client/common/stores/auth'
import { useFiltersStore } from '~/client/common/stores/filters'
import { useSpaces } from '~/client/space/use-spaces.hook'
import { ENABLE_SPACE_COUNT_PRECALCULATION } from '~/client/common/envvars'
import { useSpacesCount } from '~/client/space/use-spaces-count.hook'
import { Button } from '~/client/components/ui/button'
import { Collapsible, CollapsibleContent } from '~/client/components/ui/collapsible'
import HideFiltersButton from '~/client/components/browse-view/results/HideFiltersButton'
import { BxFilterIcon } from '~/client/components/icons'
import SpaceCard from '~/client/components/space/SpaceCard'
import { parseSpaceCardData } from '~/client/space/space-card.schema'
import { Skeleton } from '~/client/components/ui/skeleton'
import { Badge } from '~/client/components/ui/badge'

export const Route = createFileRoute('/admin/typesense')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    const { userDetail } = useAuthStore.getState()
    if (userDetail && !userDetail.is_superuser) {
      throw redirect({
        to: '/admin/recs',
        search: { eventId: undefined, tab: undefined, status: undefined, city: undefined, sort: undefined },
      })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places', 'marker']}>
      <TypesenseComparisonView />
    </APIProvider>
  )
}

function TypesenseComparisonView() {
  const [filtersHidden, setFiltersHidden] = React.useState(false)
  const [draftFilters, setDraftFilters] = React.useState<FiltersState | null>(null)
  const [appliedFilters, setAppliedFilters] = React.useState<FiltersState | null>(null)
  const [filtersKey, setFiltersKey] = React.useState(0)
  const [draftCount, setDraftCount] = React.useState<number | undefined>(undefined)
  const [lastAppliedAt, setLastAppliedAt] = React.useState<number>(0)
  const [isDirty, setIsDirty] = React.useState(false)
  const [isClosing, setIsClosing] = React.useState(false)
  const filtersRef = React.useRef<SpaceFilters2Handle | null>(null)

  const setStoreFilters = useFiltersStore((s) => s.setFilters)
  const resetTrigger = useFiltersStore((s) => s.resetTrigger)
  const activeVenueGroupId = useAuthStore((s) => s.userDetail?.active_venue_group)

  const appliedFiltersCount = React.useMemo(() => countAppliedFilters(appliedFilters), [appliedFilters])
  const appliedParams = React.useMemo(() => filtersToParams(appliedFilters, activeVenueGroupId), [appliedFilters, activeVenueGroupId])
  const draftParams = React.useMemo(() => filtersToParams(draftFilters, activeVenueGroupId), [draftFilters, activeVenueGroupId])

  const { count: draftSpacesCount } = useSpacesCount(ENABLE_SPACE_COUNT_PRECALCULATION ? draftParams : {})
  React.useEffect(() => {
    if (ENABLE_SPACE_COUNT_PRECALCULATION) {
      setDraftCount(typeof draftSpacesCount === 'number' ? draftSpacesCount : undefined)
    } else {
      setDraftCount(undefined)
    }
  }, [draftSpacesCount])

  const {
    data: supabaseData,
    count: supabaseCount,
    isLoading: isSupabaseLoading,
    isFetching: isSupabaseFetching,
    isLoadingAttachments: isSupabaseLoadingAttachments,
    error: supabaseError,
  } = useSpaces(appliedParams)

  const {
    data: typesenseData,
    count: typesenseCount,
    isLoading: isTypesenseLoading,
    isFetching: isTypesenseFetching,
    isLoadingAttachments: isTypesenseLoadingAttachments,
    error: typesenseError,
  } = useSpaces(appliedParams)

  const supabaseItems = React.useMemo(() => (Array.isArray(supabaseData) ? supabaseData : []), [supabaseData])
  const typesenseItems = React.useMemo(() => (Array.isArray(typesenseData) ? typesenseData : []), [typesenseData])

  const supabaseIdSet = React.useMemo(
    () =>
      new Set(
        supabaseItems
          .map((item: any) => item?.id)
          .filter((id): id is number => typeof id === 'number')
      ),
    [supabaseItems]
  )
  const typesenseIdSet = React.useMemo(
    () =>
      new Set(
        typesenseItems
          .map((item: any) => item?.id)
          .filter((id): id is number => typeof id === 'number')
      ),
    [typesenseItems]
  )

  const supabaseReady = Array.isArray(supabaseData)
  const typesenseReady = Array.isArray(typesenseData)
  const canCompare = supabaseReady && typesenseReady

  const missingInSupabase = React.useMemo(() => {
    if (!canCompare) return []
    return typesenseItems
      .map((item: any) => item?.id)
      .filter((id): id is number => typeof id === 'number' && !supabaseIdSet.has(id))
  }, [typesenseItems, supabaseIdSet, canCompare])

  const missingInTypesense = React.useMemo(() => {
    if (!canCompare) return []
    return supabaseItems
      .map((item: any) => item?.id)
      .filter((id): id is number => typeof id === 'number' && !typesenseIdSet.has(id))
  }, [supabaseItems, typesenseIdSet, canCompare])

  const typesenseOrderedIds = React.useMemo(
    () =>
      typesenseItems
        .map((item: any) => item?.id)
        .filter((id): id is number => typeof id === 'number'),
    [typesenseItems]
  )

  const supabaseOrderedIds = React.useMemo(
    () =>
      supabaseItems
        .map((item: any) => item?.id)
        .filter((id): id is number => typeof id === 'number'),
    [supabaseItems]
  )

  const alignmentIds = React.useMemo(() => {
    const seen = new Set<number>()
    const merged: number[] = []
    const maxLen = Math.max(typesenseOrderedIds.length, supabaseOrderedIds.length)
    for (let idx = 0; idx < maxLen; idx++) {
      const leftId = typesenseOrderedIds[idx]
      if (typeof leftId === 'number' && !seen.has(leftId)) {
        merged.push(leftId)
        seen.add(leftId)
      }
      const rightId = supabaseOrderedIds[idx]
      if (typeof rightId === 'number' && !seen.has(rightId)) {
        merged.push(rightId)
        seen.add(rightId)
      }
    }
    return merged
  }, [typesenseOrderedIds, supabaseOrderedIds])

  const resetFilters = React.useCallback(() => {
    setDraftFilters(null)
    setAppliedFilters(null)
    setFiltersKey((k) => k + 1)
    setDraftCount(undefined)
    setLastAppliedAt(0)
    setIsDirty(false)
    setIsClosing(false)
    filtersRef.current?.reset?.()
  }, [])

  const prevVenueGroupIdRef = React.useRef<number | undefined>(activeVenueGroupId)
  React.useEffect(() => {
    if (prevVenueGroupIdRef.current !== undefined && prevVenueGroupIdRef.current !== activeVenueGroupId) {
      resetFilters()
    }
    prevVenueGroupIdRef.current = activeVenueGroupId
  }, [activeVenueGroupId, resetFilters])

  const prevResetTriggerRef = React.useRef<number>(resetTrigger)
  React.useEffect(() => {
    if (prevResetTriggerRef.current !== undefined && prevResetTriggerRef.current !== resetTrigger) {
      resetFilters()
    }
    prevResetTriggerRef.current = resetTrigger
  }, [resetTrigger, resetFilters])

  const handleApplyFilters = React.useCallback(() => {
    setAppliedFilters(draftFilters)
    setLastAppliedAt(Date.now())
    setIsDirty(false)
  }, [draftFilters])

  const handleCancelFilters = React.useCallback(() => {
    setDraftFilters(appliedFilters)
    filtersRef.current?.markApplied?.()
  }, [appliedFilters])

  const toggleFiltersHidden = React.useCallback(() => setFiltersHidden((s) => !s), [])

  const resolvedSupabaseCount = typeof supabaseCount === 'number' && supabaseCount >= 0 ? supabaseCount : supabaseItems.length
  const resolvedTypesenseCount = typeof typesenseCount === 'number' && typesenseCount >= 0 ? typesenseCount : typesenseItems.length

  const applyButtonLoading = ENABLE_SPACE_COUNT_PRECALCULATION && !!draftCount && draftCount < 0
  const applyButtonDisabled = ENABLE_SPACE_COUNT_PRECALCULATION && !!draftCount && draftCount < 0

  const columnScrollRefs = React.useRef<(HTMLDivElement | null)[]>([null, null])
  const isSyncingScrollRef = React.useRef(false)

  const getScrollHandlers = React.useCallback(
    (index: number) => {
      const setRef = (node: HTMLDivElement | null) => {
        columnScrollRefs.current[index] = node
      }

      const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (isSyncingScrollRef.current) return
        const target = event.currentTarget
        isSyncingScrollRef.current = true
        columnScrollRefs.current.forEach((node, nodeIndex) => {
          if (!node || nodeIndex === index) return
          if (node.scrollTop !== target.scrollTop) {
            node.scrollTop = target.scrollTop
          }
          if (node.scrollLeft !== target.scrollLeft) {
            node.scrollLeft = target.scrollLeft
          }
        })
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            isSyncingScrollRef.current = false
          })
        } else {
          isSyncingScrollRef.current = false
        }
      }

      return { setRef, handleScroll }
    },
    []
  )

  const typesenseScrollSync = React.useMemo(() => getScrollHandlers(0), [getScrollHandlers])
  const supabaseScrollSync = React.useMemo(() => getScrollHandlers(1), [getScrollHandlers])

  return (
    <div className="w-full h-full bg-white">
      <div className="w-full h-full min-h-0 flex bg-[#fefbf6]">
        <div
          className={`h-full border-r border-gray-200 overflow-hidden flex flex-col transition-all duration-300 ease-in-out ${filtersHidden ? 'w-0 min-w-0 opacity-0 pointer-events-none' : 'w-[344px] min-w-[344px] opacity-100 pointer-events-auto'
            }`}
        >
          <div className="sticky top-0 z-[10] bg-[#fefbf6] flex items-center justify-between px-[24px] min-h-[76px] border-[#dcd8d7]">
            <div className="font-['Inter:Semi_Bold',_sans-serif] font-semibold text-[#322f36] text-[18px]">Search Filters</div>
            <button
              type="button"
              className="flex items-center gap-[4px]"
              onClick={() => {
                resetFilters()
                filtersRef.current?.markApplied?.()
              }}
            >
              <div className="size-[18px]">
                <BxFilterIcon className="block size-full" />
              </div>
              <span className="text-[14px] text-[#322f36]">Reset All</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto rs-scrollbar-minimal">
            <SpaceFilters2
              ref={filtersRef}
              key={String(filtersKey)}
              value={draftFilters ?? null}
              onChange={(next) => {
                setDraftFilters(next)
                try {
                  setStoreFilters(next)
                } catch { }
              }}
              lastAppliedAt={lastAppliedAt}
              onDirtyChange={(dirty) => {
                setIsDirty(dirty)
              }}
              venueGroupId={activeVenueGroupId}
              onApply={handleApplyFilters}
            />
          </div>
          <Collapsible open={isDirty || isClosing}>
            <CollapsibleContent
              className={[
                'sticky bottom-0 bg-white px-[16px] border-t border-[#E4E1E9] flex justify-end gap-[8px] transition-all duration-300 ease-out overflow-hidden transform',
                isDirty && !isClosing
                  ? 'py-[20px] max-h-[96px] opacity-100 translate-y-0'
                  : isClosing
                    ? 'py-[20px] max-h-[96px] opacity-0 translate-y-full pointer-events-none'
                    : 'py-0 max-h-0 opacity-0 translate-y-full pointer-events-none',
              ].join(' ')}
            >
              <Button
                variant="outline"
                onClick={() => {
                  setIsClosing(true)
                  handleCancelFilters()
                  window.setTimeout(() => {
                    setIsClosing(false)
                    setIsDirty(false)
                  }, 300)
                }}
                className="rounded-[12px] bg-white hover:bg-gray-100 border border-[#8A3DFF] text-[#8A3DFF] px-[12px] py-[8px] text-[14px]"
              >
                Cancel
              </Button>
              <Button
                isLoading={applyButtonLoading}
                disabled={applyButtonDisabled}
                onClick={() => {
                  handleApplyFilters()
                  filtersRef.current?.markApplied?.()
                }}
                className="rounded-[12px] bg-[#8A3DFF] text-white px-[12px] py-[8px] text-[14px] font-bold"
              >
                {ENABLE_SPACE_COUNT_PRECALCULATION && typeof draftCount === 'number' ? `Apply (${draftCount} Spaces)` : 'Apply'}
              </Button>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <div className="px-[24px] py-[16px] flex items-center justify-between border-b border-[#e6e2dd]">
            <HideFiltersButton icon={BxFilterIcon} label={filtersHidden ? 'Show Filters' : 'Hide Filters'} count={appliedFiltersCount} isHidden={filtersHidden} onClick={toggleFiltersHidden} />
            <div className="text-[14px] text-[#6a6262]">{appliedFiltersCount > 0 ? `${appliedFiltersCount} filters applied` : 'No filters applied'}</div>
          </div>

          <div className="flex-1 min-h-0 p-6 flex flex-col gap-4 overflow-hidden">
            {canCompare && (
              <DifferencesSummary missingInSupabase={missingInSupabase} missingInTypesense={missingInTypesense} />
            )}
            {!canCompare && (
              <div className="rounded-[16px] border border-dashed border-[#dcd8d7] bg-white px-4 py-3 text-sm text-[#6a6262]">
                Adjust filters and click Apply to see a side-by-side comparison.
              </div>
            )}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 flex-1 min-h-0">
              <ComparisonColumn
                label="Typesense Results"
                description="Live search powered by Typesense"
                items={typesenseItems}
                count={resolvedTypesenseCount}
                isLoading={isTypesenseLoading}
                isFetching={isTypesenseFetching}
                error={typesenseError}
                unmatchedIds={missingInSupabase}
                counterpartReady={supabaseReady}
                counterpartLabel="Supabase"
                isLoadingAttachments={isTypesenseLoadingAttachments}
                scrollContainerRef={typesenseScrollSync.setRef}
                onScrollSync={typesenseScrollSync.handleScroll}
                unmatchedTone="positive"
                alignmentIds={alignmentIds}
              />
              <ComparisonColumn
                label="Supabase Results"
                description="Postgres canonical list"
                items={supabaseItems}
                count={resolvedSupabaseCount}
                isLoading={isSupabaseLoading}
                isFetching={isSupabaseFetching}
                error={supabaseError}
                unmatchedIds={missingInTypesense}
                counterpartReady={typesenseReady}
                counterpartLabel="Typesense"
                isLoadingAttachments={isSupabaseLoadingAttachments}
                scrollContainerRef={supabaseScrollSync.setRef}
                onScrollSync={supabaseScrollSync.handleScroll}
                unmatchedTone="negative"
                alignmentIds={alignmentIds}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type ComparisonColumnProps = {
  label: string
  description: string
  items: any[]
  count: number
  isLoading: boolean
  isFetching: boolean
  error?: unknown
  unmatchedIds: number[]
  counterpartReady: boolean
  counterpartLabel: string
  isLoadingAttachments?: boolean
  scrollContainerRef?: (node: HTMLDivElement | null) => void
  onScrollSync?: (event: React.UIEvent<HTMLDivElement>) => void
  unmatchedTone?: 'positive' | 'negative'
  alignmentIds?: number[]
}

function ComparisonColumn({
  label,
  description,
  items,
  count,
  isLoading,
  isFetching,
  error,
  unmatchedIds,
  counterpartReady,
  counterpartLabel,
  isLoadingAttachments,
  scrollContainerRef,
  onScrollSync,
  unmatchedTone,
  alignmentIds,
}: ComparisonColumnProps) {
  const showLoadingSkeleton = isLoading && (!items || items.length === 0)
  const showEmptyState = !isLoading && !isFetching && (!items || items.length === 0)
  const unmatchedSet = React.useMemo(() => new Set(unmatchedIds), [unmatchedIds])
  const resolvedItems = Array.isArray(items) ? items : []
  const unmatchedToneStyles = React.useMemo(() => {
    switch (unmatchedTone) {
      case 'positive':
        return {
          badge: 'bg-[#ECFDF3] text-[#166534] border border-[#86EFAC]',
          card: 'bg-[#F0FDF4] border-[#86EFAC] ring-2 ring-[#86EFAC]/60 shadow-[0px_12px_30px_rgba(16,185,129,0.18)]',
          label: 'bg-[#DCFCE7] text-[#166534]',
        }
      case 'negative':
        return {
          badge: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]',
          card: 'bg-[#FEF2F2] border-[#FECACA] ring-2 ring-[#FCA5A5]/60 shadow-[0px_12px_30px_rgba(239,68,68,0.15)]',
          label: 'bg-[#FEE2E2] text-[#991B1B]',
        }
      default:
        return {
          badge: 'bg-[#FFF2E7] text-[#B45309]',
          card: '',
          label: 'bg-[#FFF2E7] text-[#B45309]',
        }
    }
  }, [unmatchedTone])

  const alignmentOrder = React.useMemo(() => (Array.isArray(alignmentIds) ? alignmentIds : []), [alignmentIds])
  const hasAlignment = alignmentOrder.length > 0

  const itemsById = React.useMemo(() => {
    const map = new Map<number, any>()
    resolvedItems.forEach((raw) => {
      const id = typeof (raw as any)?.id === 'number' ? (raw as any).id : undefined
      if (typeof id === 'number') {
        map.set(id, raw)
      }
    })
    return map
  }, [resolvedItems])

  const itemsWithoutIds = React.useMemo(
    () =>
      resolvedItems
        .map((raw, idx) => ({ raw, idx }))
        .filter(({ raw }) => typeof (raw as any)?.id !== 'number'),
    [resolvedItems]
  )

  const renderSpaceCard = React.useCallback(
    (raw: any, fallbackKey: React.Key) => {
      const data = parseSpaceCardData(raw as any)
      const spaceId = (raw as any)?.id
      const isUnmatched = counterpartReady && typeof spaceId === 'number' && unmatchedSet.has(spaceId)

      return (
        <div key={spaceId ?? fallbackKey} className="relative">
          {isUnmatched && (
            <div className={`absolute top-4 left-4 z-50 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold ${unmatchedToneStyles.label}`}>
              Missing from {counterpartLabel}
            </div>
          )}
          <SpaceCard
            data={data}
            isLoadingAttachments={isLoadingAttachments}
            className={isUnmatched ? `transition-colors duration-300 ${unmatchedToneStyles.card}`.trim() : undefined}
          />
        </div>
      )
    },
    [counterpartReady, unmatchedSet, unmatchedToneStyles.label, unmatchedToneStyles.card, counterpartLabel, isLoadingAttachments]
  )

  return (
    <div className="flex flex-col min-h-0 rounded-[16px] border border-[#E4E1E9] bg-white shadow-[0_10px_30px_rgba(95,102,123,0.08)]">
      <div className="px-5 py-4 border-b border-[#E4E1E9] flex items-center justify-between gap-4">
        <div>
          <div className="text-[16px] font-semibold text-[#322f36]">{label}</div>
          <div className="text-[13px] text-[#6a6262]">{description}</div>
        </div>
        <div className="text-right">
          <div className="text-[13px] font-semibold text-[#322f36]">{count} spaces</div>
          {counterpartReady && unmatchedIds.length > 0 && (
            <Badge variant="secondary" className={`mt-1 ${unmatchedToneStyles.badge}`}>
              {unmatchedIds.length} unmatched
            </Badge>
          )}
          {!counterpartReady && <div className="text-[12px] text-[#9a9191]">Waiting for {counterpartLabel}…</div>}
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={onScrollSync}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 rs-scrollbar-minimal"
      >
        {error ? (
          <ErrorState message={error instanceof Error ? error.message : 'Failed to load results'} />
        ) : showLoadingSkeleton ? (
          <LoadingList />
        ) : showEmptyState ? (
          <EmptyColumnState />
        ) : (
          <>
            {hasAlignment
              ? alignmentOrder.map((id) => {
                const raw = itemsById.get(id)
                if (raw) {
                  return renderSpaceCard(raw, `aligned-${id}`)
                }
                return <MissingCardPlaceholder key={`missing-${id}`} label={`Present in ${counterpartLabel}`} spaceId={id} />
              })
              : resolvedItems.map((raw: any, idx: number) => renderSpaceCard(raw, idx))}
            {hasAlignment &&
              itemsWithoutIds.map(({ raw, idx }) => renderSpaceCard(raw, `no-id-${idx}`))}
          </>
        )}
      </div>
    </div>
  )
}

function LoadingList({ count = 4 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="rounded-[12px] border border-[#E4E1E9] overflow-hidden">
          <Skeleton className="h-[200px] w-full rounded-none" />
          <div className="p-4 space-y-2">
            <Skeleton className="h-[16px] w-[60%]" />
            <Skeleton className="h-[16px] w-[40%]" />
            <div className="flex gap-2">
              <Skeleton className="h-[20px] w-[80px]" />
              <Skeleton className="h-[20px] w-[60px]" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyColumnState() {
  return (
    <div className="flex h-full items-center justify-center rounded-[12px] border border-dashed border-[#E4E1E9] bg-[#fdfaf5] p-6 text-center text-[#6a6262]">
      No spaces matched these filters.
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-[12px] border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#991B1B]">
      {message}
    </div>
  )
}

function MissingCardPlaceholder({ label, spaceId }: { label: string; spaceId?: number }) {
  return (
    <div className="relative">
      <div className="h-[283px] w-full rounded-[16px] border border-dashed border-[#D4D4D8] bg-[repeating-linear-gradient(135deg,#F4F4F5_0px,#F4F4F5_12px,#E4E4E7_12px,#E4E4E7_24px)] flex flex-col items-center justify-center text-center text-[#6b7280] px-6 shadow-inner">
        <div className="text-[15px] font-semibold">No data in this list</div>
        <div className="text-[13px]">{label}</div>
        {typeof spaceId === 'number' && <div className="mt-2 text-[12px] uppercase tracking-wide text-[#9CA3AF]">Space #{spaceId}</div>}
      </div>
    </div>
  )
}

function DifferencesSummary({ missingInSupabase, missingInTypesense }: { missingInSupabase: number[]; missingInTypesense: number[] }) {
  return (
    <div className="rounded-[16px] border border-[#F6D7B0] bg-[#FFF8EE] px-5 py-4 text-[14px] text-[#6a6262] flex flex-col gap-2">
      <SummaryLine label="Missing in Supabase" items={missingInSupabase} />
      <SummaryLine label="Missing in Typesense" items={missingInTypesense} />
    </div>
  )
}

function SummaryLine({ label, items }: { label: string; items: number[] }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[#B45309]">{label}:</span>
        <span>None</span>
      </div>
    )
  }

  const preview = items.slice(0, 6).join(', ')
  const remaining = items.length - 6

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold text-[#B45309]">{label}:</span>
      <span>{preview}</span>
      {remaining > 0 && <span className="text-[#B45309]">(+{remaining} more)</span>}
    </div>
  )
}
